import { MouseEvent, useCallback, useMemo, useState } from 'react';

import { CampaignModel, ProfileModel, campaignApi, profileApi } from '@pukka/api';
import { toast } from '@pukka/toast';
import { Button } from '@pukka/ui-components';
import { AxiosError } from 'axios';

import { closeModal, DetailedCampaignWithStatus, openModal, State } from './CustomerDisplay.state';
import { CustomerDisplayFormSchemaType } from './customerDisplayFormSchema';
import { constructPayload, generateErrorOutlets } from './utils';

async function editCampaign(data: CampaignModel.EditRequest) {
  return State.onUpdateCampaign(data);
}

function getOutletName(errorOutlets: number[], mappedOutlets: Record<string, ProfileModel.Outlet>) {
  return errorOutlets.map((outletId: number) => mappedOutlets[outletId]?.name);
}

function AssignOutletCell(props: DetailedCampaignWithStatus) {
  const { data: profile } = profileApi.useProfile();
  const { id, outlet_ids: outletIds, name, images, duration, end_date: endDate, start_date: startDate, status } = props;
  const [errorOutlets, setErrorOutlets] = useState<number[]>([]);

  const mappedOutlets = useMemo(() => {
    return (
      profile?.outlets?.reduce(
        (prev, acc) => {
          prev[acc.id || ''] = acc;

          return prev;
        },
        {} as Record<string, ProfileModel.Outlet>,
      ) || {}
    );
  }, [profile]);

  const mappedErrorOutlets = getOutletName(errorOutlets, mappedOutlets);

  const handleChangeOutlets = useCallback(
    (outlets: Set<ProfileModel.Outlet>) => {
      const newOutletIds: CustomerDisplayFormSchemaType['outletIds'] = [0, ...[...outlets.values()].map((outlet) => outlet.id ?? 0)];

      newOutletIds.shift();

      const fieldValues: CustomerDisplayFormSchemaType = {
        name,
        images,
        duration,
        setTimePeriod: !!endDate,
        timePeriod: {
          startDate: new Date(startDate),
          endDate: endDate ? new Date(endDate) : new Date(startDate),
        },
        outletIds: newOutletIds,
      };

      const payload = { campaignId: id, campaignData: constructPayload(fieldValues, status) };

      editCampaign(payload)
        .then(() => {
          closeModal();
          toast.success('Campaign was successfully updated');
          campaignApi.invalidateCampaigns();
        })
        .catch((err: AxiosError<CampaignModel.CampaignErrorResponse>) => {
          if (err.response?.data?.code === '0010') {
            const outletList = generateErrorOutlets(err.message);

            setErrorOutlets(outletList);

            if (State.modalData) {
              State.modalData.errorOutlets = outletList;
              State.modalData.mappedErrorOutlets = getOutletName(outletList, mappedOutlets);
            }
          } else {
            toast.error(err.message);
          }
        });
    },
    [duration, endDate, id, images, name, startDate, status, mappedOutlets],
  );

  const handleClickButton = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      openModal(outletIds, errorOutlets, mappedErrorOutlets, handleChangeOutlets);
    },
    [outletIds, errorOutlets, mappedErrorOutlets, handleChangeOutlets],
  );

  return (
    <Button variant="secondary" onClick={handleClickButton}>
      Assign Outlet
    </Button>
  );
}

export { AssignOutletCell };
